// components/recurring/recurring.components.jsx
import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RRule, rrulestr } from "rrule";
import "./recurring.styles.scss";

const Recurring = ({
  initialRRule, // string (“RRULE:…”) or RRule instance
  onRRuleChange, // callback receives updated RRULE string
  startDateRef, // optional Date for dtstart
}) => {
  // ─── STATE ─────────────────────────────────────────────────────────────────
  const [freq, setFreq] = useState(RRule.DAILY);
  const [interval, setInterval] = useState(1);

  // Weekly: array of weekday indices 0=Mo … 6=Su
  const [selectedDays, setSelectedDays] = useState([]);

  // Monthly & Yearly
  const [selectedMonthDay, setSelectedMonthDay] = useState(null); // Date
  const [selectedYearDay, setSelectedYearDay] = useState(null); // Date

  // Ends
  const [endCondition, setEndCondition] = useState("never"); // 'never'|'on'|'after'
  const [untilDate, setUntilDate] = useState(null);
  const [count, setCount] = useState(null);

  const [showMoreDetails, setShowMoreDetails] = useState(false);

  // Human-readable summary
  const [humanText, setHumanText] = useState("");

  // Map 0→RRule.MO, 1→RRule.TU … 6→RRule.SU
  const weekdayConstants = [
    RRule.MO,
    RRule.TU,
    RRule.WE,
    RRule.TH,
    RRule.FR,
    RRule.SA,
    RRule.SU,
  ];
  const weekdayLabels = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  // ─── PARSE INITIAL RULE ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!initialRRule) return;

    let ruleObj;
    try {
      ruleObj =
        typeof initialRRule === "string"
          ? rrulestr(initialRRule)
          : initialRRule;
    } catch {
      return;
    }
    // Use origOptions if available to preserve raw byweekday array
    const opt = ruleObj.origOptions || ruleObj.options;

    // freq & interval
    setFreq(typeof opt.freq === "number" ? opt.freq : RRule.DAILY);
    setInterval(typeof opt.interval === "number" ? opt.interval : 1);

    // WEEKLY: extract weekdays
    if (Array.isArray(opt.byweekday)) {
      const nums = opt.byweekday
        .map((wdObj) =>
          wdObj && typeof wdObj.weekday === "number" ? wdObj.weekday : null,
        )
        .filter((n) => typeof n === "number");
      setSelectedDays(nums);
    } else {
      setSelectedDays([]);
    }

    // MONTHLY: bymonthday
    if (typeof opt.bymonthday === "number" && opt.freq === RRule.MONTHLY) {
      const d = new Date();
      d.setDate(opt.bymonthday);
      setSelectedMonthDay(d);
    } else {
      setSelectedMonthDay(null);
    }

    // YEARLY: bymonth + bymonthday
    if (
      typeof opt.bymonthday === "number" &&
      typeof opt.bymonth === "number" &&
      opt.freq === RRule.YEARLY
    ) {
      const year = new Date().getFullYear();
      const d = new Date(year, opt.bymonth - 1, opt.bymonthday);
      setSelectedYearDay(d);
    } else {
      setSelectedYearDay(null);
    }

    // ENDS
    if (opt.until instanceof Date) {
      setEndCondition("on");
      setUntilDate(opt.until);
      setCount(null);
    } else if (typeof opt.count === "number") {
      setEndCondition("after");
      setCount(opt.count);
      setUntilDate(null);
    } else {
      setEndCondition("never");
      setUntilDate(null);
      setCount(null);
    }
  }, [initialRRule]);

  // ─── REBUILD, EMIT RRULE & HUMAN‐READABLE TEXT ───────────────────────────────
  useEffect(() => {
    const options = {
      freq,
      interval: Number(interval),
      dtstart: startDateRef instanceof Date ? startDateRef : new Date(),
    };

    if (freq === RRule.WEEKLY && selectedDays.length) {
      options.byweekday = selectedDays.map((d) => weekdayConstants[d]);
    }
    if (freq === RRule.MONTHLY && selectedMonthDay instanceof Date) {
      options.bymonthday =
  typeof selectedMonthDay === "number"
    ? selectedMonthDay
    : new Date(
        selectedMonthDay,
      ).getDate();
    }
    if (freq === RRule.YEARLY && selectedYearDay instanceof Date) {
      options.bymonth = selectedYearDay.getMonth() + 1;
      options.bymonthday = selectedYearDay.getDate();
    }
    if (endCondition === "on" && untilDate instanceof Date) {
      options.until = untilDate;
    } else if (endCondition === "after" && Number(count) > 0) {
      options.count = Number(count);
    }

    try {
      const rule = new RRule(options);
      onRRuleChange(rule.toString());
      setHumanText(rule.toText());
    } catch {
      // invalid options; do nothing
      setHumanText("");
    }
  }, [
    freq,
    interval,
    selectedDays,
    selectedMonthDay,
    selectedYearDay,
    endCondition,
    untilDate,
    count,
    startDateRef,
    onRRuleChange,
  ]);

  // ─── HANDLERS ───────────────────────────────────────────────────────────────
  const handleFreqClick = (newFreq) => {
    setFreq(newFreq);
    setInterval(1);
    setSelectedDays([]);
    setSelectedMonthDay(null);
    setSelectedYearDay(null);
    setEndCondition("never");
    setUntilDate(null);
    setCount(null);
  };

  const toggleWeekday = (idx) => {
    setSelectedDays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx],
    );
  };

  const handleMoreDetailsToggle = () => {
    setShowMoreDetails((v) => !v);
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="recurring">
      {/* ----- HUMAN-READABLE SUMMARY ----- */}
      {humanText && <p className="recurring_summary">{humanText}</p>}

      {/* ----- Frequency Buttons ----- */}
      <div className="recurring__options">
        <button
          type="button"
          className={
            freq === RRule.DAILY ? "recurring_freq-active" : "recurring_freq"
          }
          onClick={() => handleFreqClick(RRule.DAILY)}
        >
          Daily
        </button>
        <button
          type="button"
          className={
            freq === RRule.WEEKLY ? "recurring_freq-active" : "recurring_freq"
          }
          onClick={() => handleFreqClick(RRule.WEEKLY)}
        >
          Weekly
        </button>
        <button
          type="button"
          className={
            freq === RRule.MONTHLY ? "recurring_freq-active" : "recurring_freq"
          }
          onClick={() => handleFreqClick(RRule.MONTHLY)}
        >
          Monthly
        </button>
        <button
          type="button"
          className={
            freq === RRule.YEARLY ? "recurring_freq-active" : "recurring_freq"
          }
          onClick={() => handleFreqClick(RRule.YEARLY)}
        >
          Yearly
        </button>
      </div>

      {/* ----- Interval Slider & Label ----- */}
      <div className="recurring_mainsettings">
        <div className="timerangeslider">
          {freq === RRule.DAILY && (
            <input
              type="range"
              min="1"
              max="45"
              value={interval}
              className="timerangeslider"
              onChange={(e) => setInterval(e.target.value)}
            />
          )}
          {freq === RRule.WEEKLY && (
            <input
              type="range"
              min="1"
              max="12"
              value={interval}
              className="timerangeslider"
              onChange={(e) => setInterval(e.target.value)}
            />
          )}
          {freq === RRule.MONTHLY && (
            <input
              type="range"
              min="1"
              max="12"
              value={interval}
              className="timerangeslider"
              onChange={(e) => setInterval(e.target.value)}
            />
          )}
          {freq === RRule.YEARLY && (
            <input
              type="range"
              min="1"
              max="5"
              value={interval}
              className="timerangeslider"
              onChange={(e) => setInterval(e.target.value)}
            />
          )}
          <p>
            {`Every ${interval} ${
              freq === RRule.DAILY
                ? "day"
                : freq === RRule.WEEKLY
                  ? "week"
                  : freq === RRule.MONTHLY
                    ? "month"
                    : "year"
            }${interval > 1 ? "s" : ""}`}
          </p>
        </div>
      </div>

      {/* ----- Weekly: Day Buttons ----- */}
      {freq === RRule.WEEKLY && (
        <div className="recurring_weekdays">
          <label>Days:</label>
          {weekdayLabels.map((lbl, idx) => (
            <button
              key={idx}
              type="button"
              className={
                selectedDays.includes(idx)
                  ? "recurring_weekday-active"
                  : "recurring_weekday"
              }
              onClick={() => toggleWeekday(idx)}
            >
              {lbl}
            </button>
          ))}
        </div>
      )}

      {/* ----- Monthly: Pick Day of Month ----- */}
      {freq === RRule.MONTHLY && (
        <div className="recurring_weekdays">
          {/* <label>Day of Month:</label> */}
          <button
            type="button"
            className="recurring_weekday"
            style={{ minWidth: "75px" }}
            onClick={() => {
              const d = new Date();
              d.setDate(1);
              setSelectedMonthDay(d);
            }}
          >
            First Day of Month
          </button>
          <DatePicker
            wrapperClassName="datepicker"
            placeholderText="Pick Day"
            dateFormat="dd"
            selected={selectedMonthDay}
            onChange={(d) => setSelectedMonthDay(d)}
          />
          <button
            type="button"
            className="recurring_weekday"
            style={{ minWidth: "75px" }}
            onClick={() => {
              const now = new Date();
              const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
              setSelectedMonthDay(last);
            }}
          >
            Last Day of Month
          </button>
        </div>
      )}

      {/* ----- Yearly: Pick Month + Day ----- */}
      {freq === RRule.YEARLY && (
        <div className="recurring_weekdays">
          <button
            type="button"
            className="recurring_weekday"
            style={{ minWidth: "75px" }}
            onClick={() => {
              const now = new Date();
              const first = new Date(now.getFullYear(), 0, 1);
              setSelectedYearDay(first);
            }}
          >
            First Day of Year
          </button>
          <DatePicker
            wrapperClassName="datepicker"
            placeholderText="Pick Date"
            dateFormat="MMM d"
            selected={selectedYearDay}
            onChange={(d) => setSelectedYearDay(d)}
          />
          <button
            type="button"
            className="recurring_weekday"
            style={{ minWidth: "75px" }}
            onClick={() => {
              const now = new Date();
              const last = new Date(now.getFullYear(), 11, 31);
              setSelectedYearDay(last);
            }}
          >
            Last Day of Year
          </button>
        </div>
      )}

      {/* ----- More Details: Ends ----- */}
      {showMoreDetails && (
        <div className="recurring__end">
          <label>Ends:</label>
          <div className="recurring__endrow">
            <div className="recurring__endrowoption">
              <input
                type="radio"
                id="never"
                name="endtype"
                checked={endCondition === "never"}
                onChange={() => setEndCondition("never")}
              />
              <label htmlFor="never">Never</label>
            </div>

            <div className="recurring__endrowdate">
              <input
                type="radio"
                id="on"
                name="endtype"
                checked={endCondition === "on"}
                onChange={() => {
                  setEndCondition("on");
                  setCount(null);
                }}
              />
              <label htmlFor="on">On</label>
              <DatePicker
                wrapperClassName="datepicker"
                placeholderText="Select end date"
                dateFormat="MMMM d, yyyy"
                selected={untilDate}
                onChange={(d) => setUntilDate(d)}
              />
            </div>

            <div className="recurring__endrowoption">
              <input
                type="radio"
                id="after"
                name="endtype"
                checked={endCondition === "after"}
                onChange={() => {
                  setEndCondition("after");
                  setCount(2);
                  setUntilDate(null);
                }}
              />
              <label htmlFor="after">After</label>
              <input
                type="number"
                min="1"
                className="interval_number"
                value={count || ""}
                onChange={(e) => setCount(e.target.value)}
              />
              <label>times</label>
            </div>
          </div>
        </div>
      )}

      {/* ----- Toggle More Details ----- */}
      <button
        type="button"
        className="recurring__showmore-btn"
        onClick={handleMoreDetailsToggle}
      >
        {showMoreDetails ? "less details" : "more details"}
      </button>
    </div>
  );
};

export default Recurring;
